import { useState, useRef } from "react";

import { message } from "antd";

import useRequest from "./useRequest";

const useTransfer = () => {
  // Refs to hold AbortControllers for ongoing requests
  const getPaymentsAbortController = useRef(null);
  const getBoxesAbortController = useRef(null);

  const [debts, setDebts] = useState();
  const [debt, setDebt] = useState();
  const [boxAmounts, setBoxAmounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [transfer, setTransfer] = useState([]);
  const [payments, setPayments] = useState([]);
  const [companyDebts, setCompanyDebts] = useState();
  const [transfersByBox, setTransfersByBox] = useState({ boxes: [], filters: {}, transfer_types: [] });

  const [loading, setLoading] = useState({
    get: false,
    post: false,
    put: false,
    delete: false,
  });

  const { request } = useRequest();

  // CRRUD Transfer

  async function confirmTransfer(body) {
    setLoading({ ...loading, post: true });
    return await request(
      "POST",
      "transfers/",
      (data) => {
        setLoading({ ...loading, post: false });
        message.success("Transferencia confirmada con éxito");
        return data;
      },
      (error) => {
        setLoading({ ...loading, post: false });
        message.error("Error al confirmar la transferencia");
        return error;
      },
      body
    );
  }

  async function getTransferDetail(transferId) {
    setLoading((prev) => ({ ...prev, get: true }));

    return await request(
      "GET",
      `transfers/${transferId}/`,
      (data) => {
        setTransfer(data);

        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {}
    );
  }

  async function getTransfers(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    return await request(
      "GET",
      "transfers/",
      (data) => {
        setTransfers(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  async function updateTransfer(transferId, body) {
    setLoading({ ...loading, put: true });
    return await request(
      "PUT",
      `transfers/${transferId}/`,
      (data) => {
        setLoading({ ...loading, put: false });
        message.success("Transferencia editada con éxito");
        return data;
      },
      (error) => {
        setLoading({ ...loading, put: false });
        message.error("Error al editada la transferencia");
        return error;
      },
      body
    );
  }

  async function deleteTransfer(transferId) {
    setLoading((prev) => ({ ...prev, delete: true }));

    return await request(
      "DELETE",
      `transfers/${transferId}/`,
      (data) => {
        setTransfers((prev) => {
          const newTransfers = prev.results.filter(
            (transfer) => transfer.id !== transferId
          );

          return { ...prev, results: newTransfers };
        });

        message.success("Transferencia eliminada con éxito");

        setLoading((prev) => ({ ...prev, delete: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al eliminar la transferencia");

        return error;
      },
      {}
    );
  }

  // RR Debt

  async function getDebts(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));

    return await request(
      "GET",
      "transfers/all-debts/",
      (data) => {
        setDebts(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  async function getDebtDetail(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));

    return await request(
      "GET",
      "transfers/debts/",
      (data) => {
        setTransfer(data);

        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  async function getCompanyDebts(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));

    return await request(
      "GET",
      "transfers/all-company-debts/",
      (data) => {
        setCompanyDebts(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  // R Boxes

  async function getBoxesAmount(params = {}) {
    // Abort previous request if any
    getBoxesAbortController.current?.abort();
    // Create a new controller for the current request
    const controller = new AbortController();
    getBoxesAbortController.current = controller;

    return await request(
      "GET",
      "transfers/payments/box-amount/",
      (data) => {
        setBoxAmounts(data);
        getBoxesAbortController.current = null; // Clear controller on success
        return data;
      },
      (error) => {
        // Ignore abort errors, handle others
        if (error.name === "AbortError") {
          console.log("Get Boxes Amount fetch aborted");
          return; // Don't update loading state or show error for aborts
        }
        getBoxesAbortController.current = null; // Clear controller on error
        return error;
      },
      {},
      { ...params, signal: controller.signal } // Pass signal
    );
  }

  // R Payments

  async function getPayments(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    return await request(
      "GET",
      "transfers/payments/",
      (data) => {
        setPayments(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        // Ignore abort errors, handle others
        if (error.name === "AbortError") {
          console.log("Get Payments fetch aborted");
          return; // Don't update loading state or show error for aborts
        }
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      { ...params } // Pass signal
    );
  }

  // CD Expense

  async function createTransferExpense(body) {
    setLoading((prev) => ({ ...prev, post: true }));
    return await request(
      "POST",
      "scheduled-excursions-buses/expenses/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Gasto creado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al crear el gasto");
        return error;
      },
      body
    );
  }

  async function deleteTransferExpense(expenseId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    return await request(
      "DELETE",
      `scheduled-excursions-buses/expenses/${expenseId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Gasto eliminado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al eliminar el gasto");
        return error;
      }
    );
  }

  // Clear cache
  async function clearCache() {
    setLoading((prev) => ({ ...prev, post: true }));
    return await request(
      "POST",
      "transfers/clear-cache/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Cache actualizado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al actualizar el cache");
        return error;
      },
      {}
    );
  }

  async function getTransfersByBox(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    return await request(
      "GET",
      "transfers/by-box/",
      (data) => {
        setTransfersByBox(data);
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  async function createTransaction(body) {
    setLoading((prev) => ({ ...prev, post: true }));
    return await request(
      "POST",
      "transfers/transactions/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Transacción creada con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al crear la transacción");
        return error;
      },
      body
    );
  }

  async function getTransaction(transactionId) {
    setLoading((prev) => ({ ...prev, get: true }));
    return await request(
      "GET",
      `transfers/transactions/${transactionId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {}
    );
  }

  async function updateTransaction(transactionId, body) {
    setLoading((prev) => ({ ...prev, put: true }));
    return await request(
      "PUT",
      `transfers/transactions/${transactionId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.success("Transacción actualizada con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.error("Error al actualizar la transacción");
        return error;
      },
      body
    );
  }

  async function deleteTransaction(transactionId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    return await request(
      "DELETE",
      `transfers/transactions/${transactionId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Transacción eliminada con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al eliminar la transacción");
        return error;
      },
      {}
    );
  }

  // Función para limpiar el estado de debts (útil al cambiar de tipo)
  function clearDebts() {
    setDebts(undefined);
    setCompanyDebts(undefined);
  }

  // CRUD para gastos de vendedor (ExpenseSeller)
  async function getSellerExpenses(params = {}) {
    setLoading((prev) => ({ ...prev, get: true }));
    return await request(
      "GET",
      "passengers/expenses/",
      (data) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, get: false }));
        return error;
      },
      {},
      params
    );
  }

  async function createSellerExpense(body) {
    setLoading((prev) => ({ ...prev, post: true }));
    return await request(
      "POST",
      "passengers/expenses/",
      (data) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.success("Gasto creado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, post: false }));
        message.error("Error al crear el gasto");
        return error;
      },
      body
    );
  }

  async function updateSellerExpense(expenseId, body) {
    setLoading((prev) => ({ ...prev, put: true }));
    return await request(
      "PUT",
      `passengers/expenses/${expenseId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.success("Gasto actualizado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, put: false }));
        message.error("Error al actualizar el gasto");
        return error;
      },
      body
    );
  }

  async function deleteSellerExpense(expenseId) {
    setLoading((prev) => ({ ...prev, delete: true }));
    return await request(
      "DELETE",
      `passengers/expenses/${expenseId}/`,
      (data) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.success("Gasto eliminado con éxito");
        return data;
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        message.error("Error al eliminar el gasto");
        return error;
      },
      {}
    );
  }

  return {
    // transfers
    confirmTransfer,
    getTransfers,
    getTransferDetail,
    updateTransfer,
    deleteTransfer,
    setTransfers,
    transfer,
    transfers,

    // debts
    getDebts,
    getDebtDetail,
    getCompanyDebts,
    clearDebts,
    debt,
    debts,
    companyDebts,

    // payments
    getPayments,
    payments,

    // expenses
    createTransferExpense,
    deleteTransferExpense,

    // Boxes
    getBoxesAmount,
    boxAmounts,

    // cache
    clearCache,

    // caja (transfers by box)
    getTransfersByBox,
    transfersByBox,

    // transactions
    createTransaction,
    getTransaction,
    updateTransaction,
    deleteTransaction,

    // seller expenses
    getSellerExpenses,
    createSellerExpense,
    updateSellerExpense,
    deleteSellerExpense,

    loading,
  };
};

export default useTransfer;
